import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { FranchiseFeePayer } from 'src/models/hq/franchise-fee-payer/franchise-fee-payer';
import {
  FranchiseFeePayout,
  fetchFranchiseFeePayouts,
  fetchFranchiseFeePayoutsQueryKey,
} from 'src/models/hq/franchise-fee-payout/franchise-fee-payout';
import { formatMonthYear } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';

interface PaymentHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  payer: FranchiseFeePayer | null;
}

const PaymentHistoryDialog: React.FC<PaymentHistoryDialogProps> = ({
  open,
  onClose,
  payer,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFeePayers.paymentHistory',
  });

  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useQuery({
    queryKey: fetchFranchiseFeePayoutsQueryKey({
      page,
      perPage,
      filters: {
        payerId: payer?.id?.toString(),
        payerType: 'Hq::FranchiseFeePayer',
      },
    }),
    queryFn: () =>
      fetchFranchiseFeePayouts({
        page,
        perPage,
        sortBy: 'payoutPeriodStart',
        sortDir: 'desc',
        filters: {
          payerId: payer?.id?.toString(),
          payerType: 'Hq::FranchiseFeePayer',
        },
      }),
    enabled: open && !!payer?.id,
  });

  const getStatusColor = (
    status: FranchiseFeePayout['payoutStatus']
  ): 'default' | 'success' | 'info' | 'error' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: FranchiseFeePayout['payoutStatus']) => {
    switch (status) {
      case 'completed':
        return icon({ name: 'check-circle', style: 'solid' });
      case 'in_progress':
        return icon({ name: 'clock', style: 'solid' });
      case 'failed':
        return icon({ name: 'exclamation-circle', style: 'solid' });
      default:
        return icon({ name: 'circle', style: 'regular' });
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'payoutPeriodStart',
        label: t('period'),
        sortable: false,
        format: (value: string, row: FranchiseFeePayout) => (
          <Typography variant="body2">
            {formatMonthYear(new Date(row.payoutPeriodStart))}
          </Typography>
        ),
      },
      {
        id: 'payerPlatformName',
        label: t('platform'),
        sortable: false,
        format: (value: string) => (
          <Typography variant="body2">{value || '-'}</Typography>
        ),
      },
      {
        id: 'payoutStatus',
        label: t('status'),
        sortable: false,
        format: (value: FranchiseFeePayout['payoutStatus']) => (
          <Chip
            label={t(`statuses.${value}`)}
            color={getStatusColor(value)}
            size="small"
            icon={<Icon icon={getStatusIcon(value)} size="small" />}
          />
        ),
      },
      {
        id: 'franchiseFesTotalInDollars',
        label: t('fees'),
        sortable: false,
        format: (value: number) => (
          <Typography variant="body2">{formatCurrency(value)}</Typography>
        ),
      },
      {
        id: 'adjustmentsTotalInDollars',
        label: t('adjustments'),
        sortable: false,
        format: (value: number) => (
          <Typography
            variant="body2"
            color={
              value > 0 ? 'success.main' : value < 0 ? 'error.main' : undefined
            }
          >
            {formatCurrency(value)}
          </Typography>
        ),
      },
      {
        id: 'amountOwedInDollars',
        label: t('total'),
        sortable: false,
        format: (value: number) => (
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(value)}
          </Typography>
        ),
      },
    ],
    [t]
  );

  const rows = data?.franchiseFeePayouts || [];
  const total = data?.total || 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title', { name: payer?.name || '' })}
      maxWidth="lg"
      fullWidth
      actions={[
        {
          label: t('close'),
          onClick: onClose,
          variant: 'text',
        },
      ]}
    >
      <Box sx={{ minHeight: 400 }}>
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Stack>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <Icon
              icon={icon({ name: 'receipt', style: 'regular' })}
              sx={{ fontSize: 48, color: 'text.disabled' }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              {t('noPayments')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('noPaymentsDescription')}
            </Typography>
          </Box>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              loading={isLoading}
              noResultsText={t('noPayments')}
              totalItems={total}
              itemsPerPage={perPage}
            />

            {total > perPage && (
              <Box sx={{ mt: 2 }}>
                <SearchParamsPagination
                  params={{
                    page,
                    perPage,
                    search: '',
                    sortBy: 'payoutPeriodStart',
                    sortDir: 'desc' as const,
                  }}
                  updateParam={(key, value) => {
                    if (key === 'page') handlePageChange(Number(value));
                  }}
                  total={total}
                  itemsPerPage={perPage}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PaymentHistoryDialog;
